import type { FaceKey, ResolvedEditAreaDefinition } from '@repo/api-contracts';
import { componentRectOnPhoto } from '@repo/design-renderer';
import type { PhotoBackdropRect } from './flat-mode-mapping';
import { warpedRectOnPhoto } from './warped-view';

/** 목업 위 바로 편집의 배경 — 지금 색상·면의 목업 사진(관리자가 색상×면마다 올린 사진)과 그 위 인쇄 영역 자리. */
export interface FaceBackdrop extends PhotoBackdropRect {
  readonly url: string;
}

interface FaceBackdropInput {
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly faceKey: FaceKey;
  readonly componentId: string;
}

/**
 * 고객 에디터가 편집 표면 뒤에 깔 목업 사진과 인쇄 영역 자리(사진 px). 상품 썸네일·대표 이미지가 아니라
 * 편집영역 정의의 색상별 면 사진(photoUrl)만 쓴다. 사진·배치가 없으면 null 이다.
 */
export const faceBackdropOf = ({
  definition,
  colorOptionId,
  faceKey,
  componentId,
}: FaceBackdropInput): FaceBackdrop | null => {
  const photo = definition.colors.find((entry) => entry.colorOptionId === colorOptionId)?.faces[
    faceKey
  ];
  const face = definition.faces[faceKey];
  const placement = face?.placements.find((candidate) => candidate.componentId === componentId);
  const surface = face?.surfaces.find((candidate) => candidate.id === placement?.surfaceId);
  const component = definition.components.find((candidate) => candidate.id === componentId);
  if (
    photo === undefined ||
    placement === undefined ||
    surface === undefined ||
    component === undefined
  )
    return null;
  return {
    url: photo.photoUrl,
    photo: photo.photoSize,
    // 곡면(cylinder·mesh)은 휜 외곽의 외접 상자다.
    rect:
      surface.type === 'flat'
        ? componentRectOnPhoto(surface, photo.photoSize, component, placement.position)
        : warpedRectOnPhoto({ component, surface, position: placement.position }, photo.photoSize),
  };
};
